import {
  QueryKey,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import {
  ApprovalWorkflowControllerCountApprovalWorkflowsParams,
  ApprovalWorkflowControllerDeleteParams,
  ApprovalWorkflowControllerGetApprovalWorkflowParams,
  ApprovalWorkflowControllerListApprovalWorkflowsParams,
  ApprovalWorkflowControllerListPotentialManagersParams,
  ApprovalWorkflowItemDto,
  AssetAwCommentsControllerGetAssetCommentsParams,
  GetAssetVersionCommentsSuccessDto,
  CreateAWDto,
  GetAWSuccessDto,
  RestartAWDto,
  SendAWInvitationsDto,
  SplitAWDto,
  SubmitDecisionDto,
  UpdateAWDto
} from 'src/api/Api';
import {
  getItemPlaceholderFromListQuery,
  getPlaceholderQuery
} from '@hooks/queries/helpers';
import {
  approvalWorkflowSplit,
  assetAwCommentsGetAssetComments,
  createApprovalWorkflow,
  decisionApprovalWorkflow,
  deleteApprovalWorkflow,
  getApprovalWorkflow,
  getCountApprovalWorkflows,
  getListApprovalWorkflows,
  getPotentialsApprovalManagers,
  restartApprovalWorkflow,
  sendReminderMemberApprovalStage,
  updateApprovalWorkflow
} from '@api/Approval';

export const QUERY_APPROVAL_APPROVAL_WORKFLOW = 'approval_approval_workflow';

export const QUERY_APPROVAL_COUNT_APPROVAL_WORKFLOWS =
  'approval_count_approval_workflows';
export const QUERY_APPROVAL_LIST_APPROVAL_WORKFLOWS =
  'approval_list_approval_workflows';
export const QUERY_APPROVAL_TEMPLATES_LIST = 'approval_templates_list';
export const QUERY_APPROVAL_BATCH_ASSETS_LIST = 'approval_assets_list';
export const QUERY_APPROVAL_POTENTIALS_MANAGERS =
  'approval_potentials_managers';
export const QUERY_APPROVAL_COMMENTS_ASSETS = 'comments_assets';

export function useGetApprovalWorkflow({
  params,
  initialData,
  enabled = true
}: {
  initialData?: GetAWSuccessDto;
  params: ApprovalWorkflowControllerGetApprovalWorkflowParams;
  enabled?: boolean;
}) {
  const fetch = useQuery({
    queryKey: [QUERY_APPROVAL_APPROVAL_WORKFLOW, { params }],
    enabled: enabled && !!params?.id,
    initialData,
    retry: false,
    queryFn: async ({ signal }) => {
      const { data } = await getApprovalWorkflow(params, {
        signal
      });
      return data;
    },
    placeholderData: () => {
      const approval = getItemPlaceholderFromListQuery<ApprovalWorkflowItemDto>(
        [QUERY_APPROVAL_LIST_APPROVAL_WORKFLOWS],
        params.id
      );
      if (!approval) return undefined;
      return {
        approvalWorkflow: approval,
        commentMentions: false
      };
    }
  });

  return fetch;
}

export function useGetCountApprovalWorkflows({
  params,
  enabled = true,
  keepPreviousData
}: {
  params: ApprovalWorkflowControllerCountApprovalWorkflowsParams;
  enabled?: boolean;
  keepPreviousData?: boolean;
}) {
  const fetch = useQuery({
    queryKey: [QUERY_APPROVAL_COUNT_APPROVAL_WORKFLOWS, { params }],
    enabled: enabled && !!params?.workspaceId,
    keepPreviousData,
    retry: false,
    queryFn: async ({ signal }) => {
      const { data } = await getCountApprovalWorkflows(params, {
        signal
      });
      return data.count;
    }
  });

  return fetch;
}

export function useGetListApprovalWorkflows({
  params,
  enabled = true,
  keepPreviousData
}: {
  params: ApprovalWorkflowControllerListApprovalWorkflowsParams;
  enabled?: boolean;
  keepPreviousData?: boolean;
}) {
  const fetch = useInfiniteQuery({
    queryKey: [QUERY_APPROVAL_LIST_APPROVAL_WORKFLOWS, { params }],
    enabled: enabled && !!params.workspaceId,
    keepPreviousData,
    queryFn: async ({ pageParam, signal }) => {
      const { data } = await getListApprovalWorkflows(
        {
          ...params,
          after: pageParam
        },
        { signal }
      );
      return data;
    },
    getNextPageParam: (lastPage) =>
      lastPage.hasNext ? lastPage.endCursor : undefined
  });

  return fetch;
}

export function useUpdateApprovalWorkflow({
  queryKey = undefined
}: {
  queryKey?: QueryKey;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (params: UpdateAWDto) => {
      return await updateApprovalWorkflow(params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_APPROVAL_BATCH_ASSETS_LIST]
      });
      if (!queryKey) return;
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return mutation;
}

export function useSendReminderMemberApprovalStage({
  queryKey = undefined
}: {
  queryKey?: QueryKey;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (params: SendAWInvitationsDto) => {
      return await sendReminderMemberApprovalStage(params);
    },
    onSuccess: () => {
      if (!queryKey) return;
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return mutation;
}

export function useDecisionApprovalWorkflow({
  queryKey = undefined
}: {
  queryKey?: QueryKey;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (params: SubmitDecisionDto) => {
      return await decisionApprovalWorkflow(params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_APPROVAL_APPROVAL_WORKFLOW]
      });
      if (!queryKey) return;
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return mutation;
}

export function useRestartApprovalWorkflow({
  queryKey = undefined
}: {
  queryKey?: QueryKey;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (params: RestartAWDto) => {
      return await restartApprovalWorkflow(params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_APPROVAL_BATCH_ASSETS_LIST]
      });
      if (!queryKey) return;
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return mutation;
}

export function useDeleteApprovalWorkflow({
  queryKey = undefined
}: {
  queryKey?: QueryKey;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (params: ApprovalWorkflowControllerDeleteParams) => {
      return await deleteApprovalWorkflow(params);
    },
    onSettled: () => {
      if (!queryKey) return;
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return mutation;
}

export function useCreateApprovalWorkflow({
  queryKey = undefined
}: {
  queryKey?: QueryKey;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (data: CreateAWDto) => {
      return await createApprovalWorkflow(data);
    },
    onSuccess: () => {
      if (!queryKey) return;
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return mutation;
}

export function useGetPotentialsApprovalManagers({
  params,
  enabled = true
}: {
  params: ApprovalWorkflowControllerListPotentialManagersParams;
  enabled?: boolean;
}) {
  const fetch = useInfiniteQuery({
    queryKey: [QUERY_APPROVAL_POTENTIALS_MANAGERS, { params }],
    enabled: enabled && !!params.workspaceId,
    queryFn: async ({ pageParam, signal }) => {
      const { data } = await getPotentialsApprovalManagers(
        {
          ...params,
          after: pageParam
        },
        { signal }
      );
      return data;
    },
    getNextPageParam: (lastPage) =>
      lastPage.hasNext ? lastPage.endCursor : undefined
  });

  return fetch;
}

export function useApprovalWorkflowSplit({
  queryKey = undefined
}: {
  queryKey?: QueryKey;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (params: SplitAWDto) => {
      return await approvalWorkflowSplit(params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_APPROVAL_APPROVAL_WORKFLOW]
      });
      if (!queryKey) return;
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return mutation;
}

export function useAwCommentsAssetsQuery(
  params: AssetAwCommentsControllerGetAssetCommentsParams,
  options?: {
    enabled?: boolean;
    keepPreviousData?: boolean;
    placeholderData?: GetAssetVersionCommentsSuccessDto;
  }
) {
  return useQuery({
    ...(options ?? {}),
    queryKey: [QUERY_APPROVAL_COMMENTS_ASSETS, params.assetVersionId, params],
    queryFn: async ({ signal }) => {
      const { data } = await assetAwCommentsGetAssetComments(params, {
        signal
      });
      return data;
    },
    placeholderData: () => {
      if (options?.placeholderData) return options.placeholderData;
      return getPlaceholderQuery<GetAssetVersionCommentsSuccessDto>(
        [QUERY_APPROVAL_COMMENTS_ASSETS, params.assetVersionId],
        (data): data is GetAssetVersionCommentsSuccessDto =>
          !!data &&
          typeof data === 'object' &&
          Array.isArray((data as GetAssetVersionCommentsSuccessDto).comments)
      );
    }
  });
}
